import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { UserContext } from "../../../shared/context/userContext";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import EMPButton from "../../../shared/components/EMPButton/EMPButton";
import EMPConfirmationBanner from "../../../shared/components/EMPConfirmationBanner/EMPConfirmationBanner";
import { IS_FORD_BRAND } from "../../../utils/EmpUtil";
import DealerOnboardingClient from "../../../clients/DealerOnboardingClient";
import styles from "./OnboardingThirdPartyFinancing.module.scss";
import AutoFiEnrollmentModal from "./AutoFiEnrollmentModal";
import MenuBook from "../../../shared/Icons/MenuBook";
import { DEALER_ONBOARDING_PROGRESS_QUERY_KEY } from "../../../shared/utils/onboardingProgressUtil";
import OnboardingPageTitle from "../../../shared/components/OnboardingPageTitle/OnboardingPageTitle";

const OnboardingThirdPartyFinancing = ({ progressRequest }) => {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);
  const isFordBrand = IS_FORD_BRAND(user?.brand);
  const queryClient = useQueryClient();

  const dealerCommonId = progressRequest?.dealerCommonId;

  const progressQueryKey = [
    DEALER_ONBOARDING_PROGRESS_QUERY_KEY,
    dealerCommonId,
  ];

  const cachedProgress = queryClient.getQueryData(progressQueryKey);
  const progressData = cachedProgress?.data ?? null;

  const routeOneId = progressData?.preferences?.routeOneId;
  const hasSavedRouteOneId = !!routeOneId && routeOneId !== "UNSET";

  const savedPref = progressData?.preferences?.thirdPartyFinancingPreference;
  const initialSavedOption =
    savedPref && savedPref !== "UNSET" ? savedPref : "";

  const [selectedEnrollmentOption, setSelectedEnrollmentOption] =
    useState(initialSavedOption);
  const [savedOption, setSavedOption] = useState(initialSavedOption);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    const pref = progressData?.preferences?.thirdPartyFinancingPreference;
    if (pref && pref !== "UNSET") {
      setSelectedEnrollmentOption(pref);
      setSavedOption(pref);
    }
  }, [progressData]);

  const saveMutation = useMutation({
    mutationFn: (preference) => {
      setSaveError(null);
      return DealerOnboardingClient.saveThirdPartyFinancingEnrollment(
        progressRequest.dealerCommonId,
        {
          updatedBy: user?.userId,
          thirdPartyFinancingPreference: preference,
        }
      );
    },
    onSuccess: (_, preference) => {
      setSavedOption(preference);
      setShowConfirmation(true);
      queryClient.invalidateQueries({
        queryKey: progressQueryKey,
        exact: true,
        refetchType: "active",
      });
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const message = err?.response?.data?.message;
      const raw = detail || message || err?.message || "save_failed";
      setSaveError(typeof raw === "string" ? raw : "save_failed");
    },
  });

  const handleEnrollmentOptionChange = (event) => {
    setSelectedEnrollmentOption(event.target.value);
    setShowConfirmation(false);
  };

  const handleSave = () => {
    if (selectedEnrollmentOption === "Yes") {
      setIsModalOpen(true);
      return;
    }
    saveMutation.mutate(selectedEnrollmentOption);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEnrollmentOption(savedOption);
  };

  const handleModalSubmit = () => {
    saveMutation.mutate("Yes");
  };

  const isSaved = !!savedOption && selectedEnrollmentOption === savedOption;

  const isSaveDisabled =
    saveMutation.isPending || !selectedEnrollmentOption || isSaved;

  const saveButtonLabel = saveMutation.isPending
    ? t(
        "DealerOnboarding.content.thirdPartyFinancing.savingButtonLabel",
        "Saving..."
      )
    : isSaved
      ? t(
          "DealerOnboarding.content.thirdPartyFinancing.savedButtonLabel",
          "Saved"
        )
      : t(
          "DealerOnboarding.content.thirdPartyFinancing.saveButtonLabel",
          "Save"
        );

  return (
    <div className="tw-bg-white tw-rounded-lg tw-p-8">
      <OnboardingPageTitle
        testId="third-party-financing-title"
        className="tw-mb-6"
      >
        {t("DealerOnboarding.content.thirdPartyFinancing.title")}
      </OnboardingPageTitle>

      <Box className={styles.contentContainer}>
        {showConfirmation && (
          <EMPConfirmationBanner
            variant="success"
            primaryText={t(
              "DealerOnboarding.content.thirdPartyFinancing.stepComplete",
              "Step is complete"
            )}
            closeAriaLabel={t(
              "DealerOnboarding.content.thirdPartyFinancing.closeConfirmationBanner",
              "Close confirmation banner"
            )}
            onClose={() => setShowConfirmation(false)}
            testId="third-party-financing-completion-success"
          />
        )}

        {saveError && (
          <EMPConfirmationBanner
            variant="error"
            primaryText={t(
              "DealerOnboarding.content.thirdPartyFinancing.saveError",
              "Could not save preference. Please try again."
            )}
            closeAriaLabel={t(
              "DealerOnboarding.content.thirdPartyFinancing.closeErrorBanner",
              "Close error banner"
            )}
            testId="third-party-financing-save-error"
          />
        )}

        <Typography component="div" className={styles.bodyText}>
          <p className={styles.copyParagraph}>
            {t("DealerOnboarding.content.thirdPartyFinancing.description")}
          </p>

          <p className={styles.copyParagraph}>
            {t("DealerOnboarding.content.thirdPartyFinancing.enableIntro")}
          </p>

          <ul className={styles.bulletList}>
            <li data-testid="third-party-financing-accept-agreement-bullet">
              <strong className={styles.boldBodyText}>
                {t(
                  "DealerOnboarding.content.thirdPartyFinancing.acceptAgreementSection.titleBold"
                )}
              </strong>{" "}
              {t(
                "DealerOnboarding.content.thirdPartyFinancing.acceptAgreementSection.descriptionPre"
              )}{" "}
              <strong className={styles.boldBodyText}>
                {t(
                  "DealerOnboarding.content.thirdPartyFinancing.acceptAgreementSection.descriptionYes"
                )}
              </strong>{" "}
              {t(
                "DealerOnboarding.content.thirdPartyFinancing.acceptAgreementSection.descriptionPost"
              )}
            </li>

            <li data-testid="third-party-financing-review-lenders-bullet">
              <strong className={styles.boldBodyText}>
                {t(
                  "DealerOnboarding.content.thirdPartyFinancing.reviewLendersSection.titleBold"
                )}
              </strong>
              {t(
                "DealerOnboarding.content.thirdPartyFinancing.reviewLendersSection.description"
              )}
            </li>
          </ul>
        </Typography>

        <Box className={styles.checklistRow}>
          <MenuBook className={styles.checklistIcon} />

          <Typography component="p" className={styles.checklistText}>
            {t(
              "DealerOnboarding.content.thirdPartyFinancing.checklistPrefix",
              "Need help? "
            )}

            <a
              className={styles.checklistLink}
              href={t(
                "DealerOnboarding.content.thirdPartyFinancing.checklistLink",
                "#"
              )}
              data-testid="third-party-financing-checklist-link"
            >
              {t(
                "DealerOnboarding.content.thirdPartyFinancing.checklistLabel",
                "View Accessory Pricing Checklist"
              )}
            </a>
          </Typography>
        </Box>

        <Typography
          component="p"
          className={styles.radioQuestion}
          data-testid="third-party-financing-enroll-question"
        >
          {t(
            "DealerOnboarding.content.thirdPartyFinancing.enrollSection.question"
          )}
        </Typography>

        <FormControl component="fieldset">
          <RadioGroup
            name="third-party-financing-enrollment"
            value={selectedEnrollmentOption}
            onChange={handleEnrollmentOptionChange}
            data-testid="third-party-financing-radio-group"
            className={styles.radioGroup}
          >
            <FormControlLabel
              value="Yes"
              control={<Radio className={styles.radioButton} />}
              label={t(
                "DealerOnboarding.content.thirdPartyFinancing.enrollSection.radioYes"
              )}
              data-testid="third-party-financing-radio-yes"
              sx={{ alignItems: "center", margin: 0 }}
            />
            <FormControlLabel
              value="No"
              control={<Radio className={styles.radioButton} />}
              label={t(
                "DealerOnboarding.content.thirdPartyFinancing.enrollSection.radioNo"
              )}
              data-testid="third-party-financing-radio-no"
              sx={{ alignItems: "center", margin: 0 }}
            />
          </RadioGroup>
        </FormControl>

        <Box className={styles.saveButtonContainer}>
          <EMPButton
            variant="contained"
            disabled={isSaveDisabled}
            onClick={handleSave}
            isFord={isFordBrand}
            testId="third-party-financing-save-button"
            className={styles.saveButton}
          >
            {saveButtonLabel}
          </EMPButton>
        </Box>

        <AutoFiEnrollmentModal
          open={isModalOpen}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
          dealerCommonId={dealerCommonId}
          hasSavedRouteOneId={hasSavedRouteOneId}
          routeOneId={routeOneId}
          isFordBrand={isFordBrand}
        />
      </Box>
    </div>
  );
};

OnboardingThirdPartyFinancing.propTypes = {
  progressRequest: PropTypes.shape({
    dealerCommonId: PropTypes.string.isRequired,
    dealerInfo: PropTypes.shape({
      dealerCommonId: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default OnboardingThirdPartyFinancing;
